import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, Plus, Wallet, ArrowUpRight, ArrowDownRight,
  Tv, ChevronLeft, Loader2, Building2, Phone, Mail,
  IndianRupee, TrendingUp, Package, RefreshCw, User,
  CreditCard, Settings2, Play, CheckCircle2, AlertTriangle,
  Search, Trash2, ChevronDown, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PartnerLCOManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('lcos');
  const [lcos, setLcos] = useState([]);
  const [packs, setPacks] = useState([]);
  const [summary, setSummary] = useState({});
  const [activations, setActivations] = useState([]);
  const [selectedLco, setSelectedLco] = useState(null);
  const [lcoDetail, setLcoDetail] = useState(null);
  const [detailTab, setDetailTab] = useState('ott_subs');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialogs
  const [showAddLco, setShowAddLco] = useState(false);
  const [showCreditWallet, setShowCreditWallet] = useState(false);
  const [showSetPricing, setShowSetPricing] = useState(null);
  const [showActivate, setShowActivate] = useState(null);
  const [showLcoDetail, setShowLcoDetail] = useState(false);
  const [disableTarget, setDisableTarget] = useState(null);

  // Forms
  const [addForm, setAddForm] = useState({ company_name: '', contact_name: '', phone: '', email: '', address: '', city: '', state: '', pincode: '' });
  const [creditAmount, setCreditAmount] = useState('');
  const [creditLcoId, setCreditLcoId] = useState('');
  const [pricingForm, setPricingForm] = useState({ lco_price: '' });
  const [activateForm, setActivateForm] = useState({ lco_customer_id: '', mobile_no: '', subscriber_name: '', subscriber_email: '' });

  // IPTV-specific state (LCOs share OTT + IPTV wallets)
  const [showCreditIptv, setShowCreditIptv] = useState(false);
  const [creditIptvAmount, setCreditIptvAmount] = useState('');
  const [iptvPartnerBalance, setIptvPartnerBalance] = useState(null); // null = unknown / IPTV not enabled
  const [iptvEnabled, setIptvEnabled] = useState(false);
  // IPTV pricing tab data
  const [iptvPackages, setIptvPackages] = useState([]);
  const [showSetIptvPrice, setShowSetIptvPrice] = useState(null); // pkg
  const [iptvPriceForm, setIptvPriceForm] = useState({ lco_price: '' });

  // Per-LCO custom pricing (inside LCO detail dialog)
  const [lcoOttPricing, setLcoOttPricing] = useState([]);
  const [lcoIptvPricing, setLcoIptvPricing] = useState([]);
  const [priceEdits, setPriceEdits] = useState({}); // key -> string value
  const [savingPriceKey, setSavingPriceKey] = useState(null);
  const [pricingLcoId, setPricingLcoId] = useState(''); // selected LCO in the LCO Pricing tab

  // Loading states
  const [submitting, setSubmitting] = useState(false);

  const basePath = window.location.pathname.startsWith('/partner') ? '/partner' : '/portal';
  const token = localStorage.getItem('portal_token');

  // Extract proper error message from any API response
  const getError = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data.detail === 'string') return data.detail;
    if (Array.isArray(data.detail)) return data.detail.map(d => d.msg || d.message).filter(Boolean).join(', ') || fallback;
    if (data.message) return data.message;
    return fallback;
  };

  const headers = useCallback(() => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  useEffect(() => {
    if (!token) { navigate(basePath); return; }
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchLcos(), fetchPricing(), fetchSummary(), fetchActivations(), fetchIptvBalance()]);
    setLoading(false);
  };

  const fetchIptvBalance = async () => {
    // Returns 403 if partner has no iptv_access_token. Treat as "IPTV not enabled".
    try {
      const res = await fetch(`${API}/api/portal/iptv/wallet/balance`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setIptvPartnerBalance(data.balance || 0);
        setIptvEnabled(true);
        // If IPTV enabled, also pull pricing
        fetchIptvPricing();
      } else {
        setIptvPartnerBalance(null);
        setIptvEnabled(false);
      }
    } catch (err) { setIptvPartnerBalance(null); setIptvEnabled(false); }
  };

  const fetchIptvPricing = async () => {
    try {
      const res = await fetch(`${API}/api/portal/iptv/pricing`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setIptvPackages(data.packages || []);
      }
    } catch (err) { /* silent */ }
  };

  const handleSetIptvPrice = async () => {
    if (!showSetIptvPrice) return;
    const amount = parseFloat(iptvPriceForm.lco_price);
    if (!amount || amount <= 0) { toast.error('Enter a valid price'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/pricing/${showSetIptvPrice.package_id}`, {
        method: 'PUT', headers: headers(), body: JSON.stringify({ lco_price: amount })
      });
      if (res.ok) {
        toast.success('IPTV price updated');
        setShowSetIptvPrice(null);
        setIptvPriceForm({ lco_price: '' });
        fetchIptvPricing();
      } else {
        const err = await res.json();
        toast.error(getError(err, 'Failed to set price'));
      }
    } catch (err) { toast.error('Connection error'); }
    finally { setSubmitting(false); }
  };

  const handleCreditIptvWallet = async () => {
    const amount = parseFloat(creditIptvAmount);
    if (!amount || amount <= 0 || !creditLcoId) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/iptv/wallet/credit-lco`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ lco_customer_id: creditLcoId, amount })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'IPTV wallet credited');
        setShowCreditIptv(false);
        setCreditIptvAmount('');
        fetchLcos();
        fetchIptvBalance();
      } else {
        const err = await res.json();
        toast.error(getError(err, 'Failed to credit IPTV wallet'));
      }
    } catch (err) { toast.error('Unable to connect to server. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const fetchLcos = async () => {
    try {
      const res = await fetch(`${API}/api/portal/lco/list`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.status === 401) { navigate(basePath); return; }
      if (res.status === 403) {
        const err = await res.json();
        toast.error(err.detail || 'Access denied');
        navigate(`${basePath}/dashboard`);
        return;
      }
      if (res.ok) {
        const data = await res.json();
        setLcos(data.lcos || []);
      }
    } catch (err) { console.error('Failed to fetch LCOs:', err); }
  };

  const fetchPricing = async () => {
    try {
      const res = await fetch(`${API}/api/portal/lco/pricing`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setPacks(data.packs || []);
      }
    } catch (err) { console.error('Failed to fetch pricing:', err); }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API}/api/portal/lco/commission-summary`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) { console.error('Failed to fetch summary:', err); }
  };

  const fetchActivations = async () => {
    try {
      const res = await fetch(`${API}/api/portal/lco/activations`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setActivations(data.activations || []);
      }
    } catch (err) { console.error('Failed to fetch activations:', err); }
  };

  const fetchLcoDetail = async (lcoId) => {
    try {
      const res = await fetch(`${API}/api/portal/lco/${lcoId}/detail`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setLcoDetail(data);
        setShowLcoDetail(true);
      }
    } catch (err) { toast.error('Failed to load LCO details'); }
  };

  // Open the dedicated full-width "LCO Pricing" tab for a specific LCO
  const openLcoPricing = (lcoId) => {
    setPricingLcoId(lcoId);
    setPriceEdits({});
    fetchLcoPricing(lcoId);
    setTab('lco_pricing');
  };

  const fetchLcoPricing = async (lcoId) => {    setPriceEdits({});
    try {
      const res = await fetch(`${API}/api/portal/lco/${lcoId}/pricing`, { headers: headers() });
      if (res.ok) { const d = await res.json(); setLcoOttPricing(d.packs || []); }
    } catch (err) { console.error('Failed OTT LCO pricing', err); }
    if (iptvEnabled) {
      try {
        const res = await fetch(`${API}/api/portal/lco/${lcoId}/iptv-pricing`, { headers: headers() });
        if (res.ok) { const d = await res.json(); setLcoIptvPricing(d.packages || []); }
      } catch (err) { console.error('Failed IPTV LCO pricing', err); }
    } else {
      setLcoIptvPricing([]);
    }
  };

  const saveLcoOverride = async (kind, id, rawValue) => {
    const lcoId = pricingLcoId;
    if (!lcoId) return;
    const value = rawValue === '' ? 0 : parseFloat(rawValue);
    if (isNaN(value) || value < 0) { toast.error('Enter a valid price (0 to clear)'); return; }
    const key = `${kind}-${id}`;
    setSavingPriceKey(key);
    try {
      const url = kind === 'ott'
        ? `${API}/api/portal/lco/${lcoId}/pricing/${id}`
        : `${API}/api/portal/lco/${lcoId}/iptv-pricing/${id}`;
      const res = await fetch(url, { method: 'PUT', headers: headers(), body: JSON.stringify({ lco_price: value }) });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Custom price saved');
        fetchLcoPricing(lcoId);
      } else {
        toast.error(getError(data, 'Failed to save custom price'));
      }
    } catch (err) {
      toast.error('Failed to save custom price');
    } finally {
      setSavingPriceKey(null);
    }
  };

  // Add LCO
  const handleAddLco = async () => {
    if (!addForm.company_name || !addForm.contact_name || !addForm.phone) {
      toast.error('Company name, contact name, and phone are required');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/lco/add`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify(addForm)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || 'LCO added successfully');
        setShowAddLco(false);
        setAddForm({ company_name: '', contact_name: '', phone: '', email: '', address: '', city: '', state: '', pincode: '' });
        fetchLcos();
        fetchSummary();
      } else {
        // Handle both 400 (our error) and 422 (Pydantic validation)
        let errorMsg = 'Failed to add LCO';
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        } else if (data.message) {
          errorMsg = data.message;
        }
        toast.error(errorMsg);
      }
    } catch (err) { 
      console.error('Add LCO error:', err);
      toast.error('Unable to connect to server. Please check your internet connection.'); 
    }
    finally { setSubmitting(false); }
  };

  // Disable (soft-delete) an LCO — preserves all data, re-creatable with same login
  const handleDisableLco = async () => {
    if (!disableTarget) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/lco/${disableTarget.customer_id}`, {
        method: 'DELETE', headers: headers()
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(data.message || 'LCO disabled');
        setDisableTarget(null);
        fetchLcos();
        fetchSummary();
      } else {
        const msg = typeof data.detail === 'string' ? data.detail : (data.message || 'Failed to disable LCO');
        toast.error(msg);
      }
    } catch (err) {
      console.error('Disable LCO error:', err);
      toast.error('Unable to connect to server. Please try again.');
    }
    finally { setSubmitting(false); }
  };

  // Credit LCO Wallet
  const handleCreditWallet = async () => {
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0 || !creditLcoId) {
      toast.error('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/lco/credit-wallet`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ lco_customer_id: creditLcoId, amount, notes: '' })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowCreditWallet(false);
        setCreditAmount('');
        fetchLcos();
        fetchSummary();
      } else {
        const err = await res.json();
        toast.error(getError(err, 'Insufficient balance or transfer failed'));
      }
    } catch (err) { toast.error('Unable to connect to server. Please try again.'); }
    finally { setSubmitting(false); }
  };

  // Set LCO Pricing
  const handleSetPricing = async () => {
    const price = parseFloat(pricingForm.lco_price);
    if (!price || price <= 0 || !showSetPricing) {
      toast.error('Enter a valid price');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/lco/pricing/${showSetPricing.pack_id}`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ pack_id: showSetPricing.pack_id, lco_price: price })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setShowSetPricing(null);
        setPricingForm({ lco_price: '' });
        fetchPricing();
      } else {
        const err = await res.json();
        toast.error(getError(err, 'Failed to set pricing. LCO price must be higher than your cost.'));
      }
    } catch (err) { toast.error('Unable to connect to server. Please try again.'); }
    finally { setSubmitting(false); }
  };

  // Activate for LCO
  const handleActivate = async () => {
    if (!activateForm.lco_customer_id || !activateForm.mobile_no || !showActivate) {
      toast.error('Select LCO and enter mobile number');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/lco/activate`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          ...activateForm,
          pack_id: showActivate.pack_id
        })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        if (data.commission_earned > 0) {
          toast.success(`Commission earned: Rs.${data.commission_earned.toFixed(2)}`);
        }
        setShowActivate(null);
        setActivateForm({ lco_customer_id: '', mobile_no: '', subscriber_name: '', subscriber_email: '' });
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(getError(err, 'Activation failed. Check LCO wallet balance and pricing.'));
      }
    } catch (err) { toast.error('Unable to connect to server. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'lco' | 'iptv_distributor'
  const [expandedDistIds, setExpandedDistIds] = useState({});

  const filteredLcos = lcos.filter(l =>
    (typeFilter === 'all' || (l.customer_type || 'lco') === typeFilter) &&
    (!searchTerm || l.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     l.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     l.contact_phone?.includes(searchTerm) ||
     l.customer_code?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const typeCounts = {
    total: lcos.length,
    lco: lcos.filter(l => (l.customer_type || 'lco') === 'lco').length,
    distributor: lcos.filter(l => l.customer_type === 'iptv_distributor').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-lco-management">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/wallet`)} data-testid="back-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" /> LCO Management
              </h1>
              <p className="text-xs text-slate-500">Manage your distributors and sub-operators</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`${basePath}/care-pricing`)}
              className="bg-white border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="care-pricing-nav-btn-lco-mgmt">
              <IndianRupee className="h-4 w-4 mr-1" /> Customer Pricing
            </Button>
            <Button onClick={() => setShowAddLco(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-lco-btn">
              <Plus className="h-4 w-4 mr-1" /> Add LCO
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {iptvEnabled && (
          <div className="flex items-center justify-between gap-3 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 text-sm">
            <div className="flex items-center gap-2 text-indigo-800">
              <Tv className="h-4 w-4" />
              <span>IPTV is enabled for your account. Manage IPTV packages, pricing and subscriptions on the dedicated IPTV page.</span>
            </div>
            <Button variant="outline" size="sm" className="bg-white border-indigo-300 text-indigo-700 hover:bg-indigo-100" onClick={() => navigate(`${basePath}/iptv`)} data-testid="goto-iptv-management">
              Manage IPTV →
            </Button>
          </div>
        )}
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card data-testid="card-lco-count">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Users className="h-4 w-4" /> Total LCOs
              </div>
              <div className="text-2xl font-bold text-slate-800">{summary.total_lcos || 0}</div>
            </CardContent>
          </Card>
          <Card data-testid="card-partner-balance">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Wallet className="h-4 w-4" /> My OTT Wallet
              </div>
              <div className="text-2xl font-bold text-green-600">
                Rs.{(summary.partner_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="card-partner-iptv-balance">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Tv className="h-4 w-4" /> My IPTV Wallet
              </div>
              {iptvEnabled ? (
                <div className="text-2xl font-bold text-indigo-600">
                  Rs.{(iptvPartnerBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              ) : (
                <div className="text-xs text-slate-400 mt-1">IPTV not enabled — contact admin to set IPTV access token.</div>
              )}
            </CardContent>
          </Card>
          <Card data-testid="card-total-commission" className="cursor-pointer hover:ring-2 hover:ring-amber-200 transition" onClick={() => navigate(`${basePath}/commissions`)}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <TrendingUp className="h-4 w-4" /> Commission Earned
              </div>
              <div className="text-2xl font-bold text-amber-600">
                Rs.{(summary.total_commission || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-slate-400 mt-1">View full dashboard →</p>
            </CardContent>
          </Card>
          <Card data-testid="card-total-activations">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                <Tv className="h-4 w-4" /> Activations
              </div>
              <div className="text-2xl font-bold text-blue-600">{summary.total_activations || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border">
            <TabsTrigger value="lcos" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Users className="h-4 w-4 mr-1" /> My LCOs
            </TabsTrigger>
            <TabsTrigger value="pricing" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Settings2 className="h-4 w-4 mr-1" /> OTT Pricing
            </TabsTrigger>
            {iptvEnabled && (
              <TabsTrigger value="iptv_pricing" className="data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-700">
                <Tv className="h-4 w-4 mr-1" /> IPTV Pricing
              </TabsTrigger>
            )}
            <TabsTrigger value="lco_pricing" className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700" data-testid="lco-pricing-tab">
              <IndianRupee className="h-4 w-4 mr-1" /> Per-LCO Pricing
            </TabsTrigger>
            <TabsTrigger value="activations" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              <Tv className="h-4 w-4 mr-1" /> Activations
            </TabsTrigger>
          </TabsList>

          {/* LCOs Tab */}
          <TabsContent value="lcos">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <CardTitle className="text-base">Sub-Network</CardTitle>
                    <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 text-xs" data-testid="lco-type-filter">
                      {[
                        { k: 'all', label: `All (${typeCounts.total})` },
                        { k: 'lco', label: `LCOs (${typeCounts.lco})` },
                        { k: 'iptv_distributor', label: `Distributors (${typeCounts.distributor})` },
                      ].map(o => (
                        <button
                          key={o.k}
                          onClick={() => setTypeFilter(o.k)}
                          data-testid={`filter-${o.k}`}
                          className={`px-3 py-1 rounded transition-colors ${
                            typeFilter === o.k
                              ? 'bg-white shadow text-slate-800 font-medium'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}
                        >{o.label}</button>
                      ))}
                    </div>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search LCOs / Distributors..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 h-9"
                      data-testid="search-lcos"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredLcos.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">
                      {typeFilter === 'iptv_distributor' ? 'No Distributors yet' :
                       typeFilter === 'lco' ? 'No LCOs yet' : 'No sub-network yet'}
                    </p>
                    <p className="text-xs mt-1">Click &quot;Add LCO&quot; to add your first sub-operator</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredLcos.map((lco) => {
                      const isDistributor = lco.customer_type === 'iptv_distributor';
                      const subLcos = lco.sub_lcos || [];
                      const isExpanded = !!expandedDistIds[lco.customer_id];
                      return (
                      <div key={lco.customer_id} data-testid={`lco-${lco.customer_id}`}>
                      <div
                        className={`flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 transition-colors ${
                          isDistributor ? 'border-l-4 border-l-purple-500' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {isDistributor && subLcos.length > 0 ? (
                            <button
                              onClick={() => setExpandedDistIds(p => ({ ...p, [lco.customer_id]: !isExpanded }))}
                              className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center hover:bg-purple-200 transition-colors"
                              data-testid={`expand-dist-${lco.customer_id}`}
                              title={isExpanded ? 'Collapse sub-LCOs' : 'Show sub-LCOs'}
                            >
                              {isExpanded
                                ? <ChevronDown className="h-5 w-5 text-purple-700" />
                                : <ChevronRight className="h-5 w-5 text-purple-700" />}
                            </button>
                          ) : (
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              isDistributor ? 'bg-purple-100' : 'bg-blue-100'
                            }`}>
                              <Building2 className={`h-5 w-5 ${isDistributor ? 'text-purple-600' : 'text-blue-600'}`} />
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-sm text-slate-800 flex items-center gap-2 flex-wrap">
                              {lco.company_name}
                              {lco.customer_code && (
                                <span
                                  className="font-mono text-[10px] text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5"
                                  data-testid={`lco-code-${lco.customer_id}`}
                                  title="LCO / Distributor code"
                                >
                                  {lco.customer_code}
                                </span>
                              )}
                              {isDistributor ? (
                                <Badge className="bg-purple-100 text-purple-800 border-purple-300 text-[10px] px-1.5 py-0" data-testid={`type-badge-${lco.customer_id}`}>
                                  DISTRIBUTOR{subLcos.length ? ` · ${subLcos.length} LCO${subLcos.length === 1 ? '' : 's'}` : ''}
                                </Badge>
                              ) : (
                                <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px] px-1.5 py-0" data-testid={`type-badge-${lco.customer_id}`}>
                                  LCO
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
                              <span className="flex items-center gap-1"><User className="h-3 w-3" /> {lco.contact_name || lco.name}</span>
                              <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {lco.contact_phone || lco.phone}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                          <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">
                            <Wallet className="h-3 w-3 mr-1" />
                            OTT Rs.{(lco.ott_wallet_balance ?? lco.wallet_balance ?? 0).toFixed(2)}
                          </Badge>
                          <Badge variant="outline" className="text-indigo-700 border-indigo-200 bg-indigo-50" data-testid={`iptv-balance-${lco.customer_id}`}>
                            <Tv className="h-3 w-3 mr-1" />
                            IPTV Rs.{(lco.iptv_wallet_balance || 0).toFixed(2)}
                          </Badge>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => { setCreditLcoId(lco.customer_id); setSelectedLco(lco); setShowCreditWallet(true); }}
                            data-testid={`credit-lco-${lco.customer_id}`}
                            title="Credit OTT wallet"
                          >
                            <CreditCard className="h-3.5 w-3.5 mr-1" /> OTT
                          </Button>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => { setCreditLcoId(lco.customer_id); setSelectedLco(lco); setCreditIptvAmount(''); setShowCreditIptv(true); }}
                            data-testid={`credit-iptv-lco-${lco.customer_id}`}
                            disabled={!iptvEnabled}
                            title={iptvEnabled ? 'Credit IPTV wallet' : 'IPTV not enabled for your account'}
                            className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                          >
                            <Tv className="h-3.5 w-3.5 mr-1" /> IPTV
                          </Button>
                          <Button
                            variant="outline" size="sm"
                            onClick={() => openLcoPricing(lco.customer_id)}
                            data-testid={`set-price-lco-${lco.customer_id}`}
                            title="Set custom OTT/IPTV price for this LCO"
                            className="text-amber-700 border-amber-200 hover:bg-amber-50"
                          >
                            <IndianRupee className="h-3.5 w-3.5 mr-1" /> Set Price
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => { setDetailTab('ott_subs'); fetchLcoDetail(lco.customer_id); }}
                            data-testid={`view-lco-${lco.customer_id}`}
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => setDisableTarget(lco)}
                            data-testid={`disable-lco-${lco.customer_id}`}
                            title="Disable / remove this LCO (data preserved)"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      {/* Expanded sub-LCOs under a Distributor (3-tier view) */}
                      {isDistributor && isExpanded && subLcos.length > 0 && (
                        <div className="ml-12 mt-1 mb-2 space-y-1 border-l-2 border-purple-200 pl-3" data-testid={`sub-lcos-${lco.customer_id}`}>
                          {subLcos.map(sub => (
                            <div
                              key={sub.customer_id}
                              className="flex items-center justify-between p-2 rounded bg-slate-50 border border-slate-200"
                              data-testid={`sub-lco-${sub.customer_id}`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="h-7 w-7 rounded-full bg-blue-100 flex items-center justify-center">
                                  <Building2 className="h-3.5 w-3.5 text-blue-600" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                                    {sub.company_name || sub.name}
                                    {sub.customer_code && (
                                      <span
                                        className="font-mono text-[9px] text-slate-500 bg-white border border-slate-200 rounded px-1 py-0.5"
                                        data-testid={`sub-lco-code-${sub.customer_id}`}
                                      >
                                        {sub.customer_code}
                                      </span>
                                    )}
                                    <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[9px] px-1 py-0">Sub-LCO</Badge>
                                  </div>
                                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                                    <span>{sub.email}</span>
                                    <span>·</span>
                                    <span>{sub.phone}</span>
                                  </div>
                                </div>
                              </div>
                              <Badge variant="outline" className="text-indigo-700 border-indigo-200 bg-indigo-50 text-xs">
                                <Tv className="h-3 w-3 mr-1" />
                                Rs.{(sub.iptv_wallet_balance || 0).toFixed(2)}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">OTT Pack Pricing</CardTitle>
                <p className="text-xs text-slate-500">Set the price you charge your LCOs. Your profit = LCO Price - Your Cost.</p>
              </CardHeader>
              <CardContent>
                {packs.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No OTT packs available</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {packs.map((pack) => (
                      <div
                        key={pack.pack_id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50"
                        data-testid={`pricing-pack-${pack.pack_id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Tv className="h-4 w-4 text-purple-600" />
                            <span className="font-medium text-sm">{pack.pack_name}</span>
                            <Badge variant="outline" className="text-xs">{pack.provider}</Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                            <span>Base: Rs.{pack.base_price}</span>
                            <span className="text-blue-600 font-medium">Your Cost: Rs.{pack.partner_price}</span>
                            <span>{pack.validity_days} days</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {pack.lco_price > 0 ? (
                            <div className="text-right">
                              <div className="text-sm font-semibold text-slate-800">LCO: Rs.{pack.lco_price}</div>
                              <div className="text-xs text-green-600 font-medium">Margin: Rs.{pack.margin.toFixed(2)}</div>
                            </div>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Not Set</Badge>
                          )}
                          <Button
                            variant="outline" size="sm"
                            onClick={() => { setShowSetPricing(pack); setPricingForm({ lco_price: pack.lco_price > 0 ? String(pack.lco_price) : '' }); }}
                            data-testid={`set-price-${pack.pack_id}`}
                          >
                            <IndianRupee className="h-3.5 w-3.5 mr-1" /> Set Price
                          </Button>
                          {pack.lco_price > 0 && (
                            <Button
                              variant="default" size="sm" className="bg-green-600 hover:bg-green-700"
                              onClick={() => { setShowActivate(pack); setActivateForm({ lco_customer_id: '', mobile_no: '', subscriber_name: '', subscriber_email: '' }); }}
                              data-testid={`activate-${pack.pack_id}`}
                            >
                              <Play className="h-3.5 w-3.5 mr-1" /> Activate
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* IPTV Pricing Tab */}
          {iptvEnabled && (
            <TabsContent value="iptv_pricing">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2"><Tv className="h-4 w-4 text-indigo-600" /> IPTV Package Pricing</CardTitle>
                  <p className="text-xs text-slate-500">Set the IPTV price you charge your LCOs. Profit = LCO Price − Your Cost.</p>
                </CardHeader>
                <CardContent>
                  {iptvPackages.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No IPTV packages available</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {iptvPackages.map((pkg) => (
                        <div key={pkg.package_id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50" data-testid={`iptv-pricing-pkg-${pkg.package_id}`}>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Tv className="h-4 w-4 text-indigo-600" />
                              <span className="font-medium text-sm">{pkg.name}</span>
                            </div>
                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                              <span>Base: Rs.{pkg.base_price}</span>
                              <span className="text-blue-600 font-medium">Your Cost: Rs.{pkg.partner_price}</span>
                              <span>{pkg.validity_days} days</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {pkg.lco_price > 0 ? (
                              <div className="text-right">
                                <div className="text-sm font-semibold text-slate-800">LCO: Rs.{pkg.lco_price}</div>
                                <div className="text-xs text-green-600 font-medium">Margin: Rs.{(pkg.margin || 0).toFixed(2)}</div>
                              </div>
                            ) : (
                              <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">Not Set</Badge>
                            )}
                            <Button variant="outline" size="sm"
                              onClick={() => { setShowSetIptvPrice(pkg); setIptvPriceForm({ lco_price: pkg.lco_price > 0 ? String(pkg.lco_price) : '' }); }}
                              data-testid={`set-iptv-price-${pkg.package_id}`}>
                              <IndianRupee className="h-3.5 w-3.5 mr-1" /> Set Price
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Per-LCO Pricing Tab (full-width, no popup) */}
          <TabsContent value="lco_pricing">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <IndianRupee className="h-4 w-4 text-amber-600" /> Per-LCO Custom Pricing
                </CardTitle>
                <CardDescription>
                  Set a different price for a specific LCO (OTT & IPTV). Leave blank / 0 to use your default LCO price. Price cannot be below your cost.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="max-w-md">
                  <label className="text-sm font-medium text-slate-700">Select LCO</label>
                  <Select
                    value={pricingLcoId}
                    onValueChange={(val) => { setPricingLcoId(val); setPriceEdits({}); fetchLcoPricing(val); }}
                  >
                    <SelectTrigger data-testid="pricing-lco-select" className="mt-1">
                      <SelectValue placeholder="Choose an LCO to set prices" />
                    </SelectTrigger>
                    <SelectContent>
                      {lcos.map((lco) => (
                        <SelectItem key={lco.customer_id} value={lco.customer_id} data-testid={`pricing-lco-option-${lco.customer_id}`}>
                          {lco.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {!pricingLcoId ? (
                  <div className="text-sm text-slate-400 py-10 text-center border rounded-md">
                    Select an LCO above to view and set custom prices.
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* OTT */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><Tv className="h-4 w-4" /> OTT Packs</h4>
                      <div className="border rounded-md overflow-hidden">
                        <div className="hidden sm:flex items-center gap-4 px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500">
                          <div className="flex-1">Package</div>
                          <div className="w-28 text-right">Your Cost</div>
                          <div className="w-28 text-right">Default</div>
                          <div className="w-36 text-center">Custom Price</div>
                          <div className="w-20" />
                        </div>
                        {lcoOttPricing.length > 0 ? lcoOttPricing.map((p) => {
                          const key = `ott-${p.pack_id}`;
                          const editVal = priceEdits[key] !== undefined ? priceEdits[key] : (p.override_price > 0 ? String(p.override_price) : '');
                          return (
                            <div key={p.pack_id} className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 px-4 py-3 border-t text-sm" data-testid={`lco-ott-price-row-${p.pack_id}`}>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium truncate flex items-center gap-2">
                                  {p.pack_name}
                                  {p.override_price > 0 && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 shrink-0">Custom</Badge>}
                                </div>
                                <div className="text-xs text-slate-400">{p.provider} · {p.validity_days}d</div>
                              </div>
                              <div className="w-28 text-right text-slate-600">Rs.{p.partner_price}</div>
                              <div className="w-28 text-right text-slate-600">{p.default_lco_price > 0 ? `Rs.${p.default_lco_price}` : '—'}</div>
                              <div className="w-36 flex justify-center">
                                <Input
                                  type="number"
                                  className="h-9 w-32 text-sm"
                                  placeholder={p.default_lco_price > 0 ? `Rs.${p.default_lco_price}` : 'Price'}
                                  value={editVal}
                                  onChange={(e) => setPriceEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                                  data-testid={`lco-ott-price-input-${p.pack_id}`}
                                />
                              </div>
                              <div className="w-20 flex items-center shrink-0">
                                <Button size="sm" className="h-9 w-full" disabled={savingPriceKey === key} onClick={() => saveLcoOverride('ott', p.pack_id, editVal)} data-testid={`lco-ott-price-save-${p.pack_id}`}>
                                  {savingPriceKey === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                                </Button>
                              </div>
                            </div>
                          );
                        }) : <div className="text-xs text-slate-400 px-4 py-3">No OTT packs</div>}
                      </div>
                    </div>

                    {/* IPTV */}
                    {iptvEnabled && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><Tv className="h-4 w-4 text-indigo-600" /> IPTV Packages</h4>
                        <div className="border rounded-md overflow-hidden">
                          <div className="hidden sm:flex items-center gap-4 px-4 py-2 bg-slate-50 text-xs font-medium text-slate-500">
                            <div className="flex-1">Package</div>
                            <div className="w-28 text-right">Your Cost</div>
                            <div className="w-28 text-right">Default</div>
                            <div className="w-36 text-center">Custom Price</div>
                            <div className="w-20" />
                          </div>
                          {lcoIptvPricing.length > 0 ? lcoIptvPricing.map((p) => {
                            const key = `iptv-${p.package_id}`;
                            const editVal = priceEdits[key] !== undefined ? priceEdits[key] : (p.override_price > 0 ? String(p.override_price) : '');
                            return (
                              <div key={p.package_id} className="flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4 px-4 py-3 border-t text-sm" data-testid={`lco-iptv-price-row-${p.package_id}`}>
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium truncate flex items-center gap-2">
                                    {p.name}
                                    {p.override_price > 0 && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 shrink-0">Custom</Badge>}
                                  </div>
                                  <div className="text-xs text-slate-400">{p.validity_days}d</div>
                                </div>
                                <div className="w-28 text-right text-slate-600">Rs.{p.partner_price}</div>
                                <div className="w-28 text-right text-slate-600">{p.default_lco_price > 0 ? `Rs.${p.default_lco_price}` : '—'}</div>
                                <div className="w-36 flex justify-center">
                                  <Input
                                    type="number"
                                    className="h-9 w-32 text-sm"
                                    placeholder={p.default_lco_price > 0 ? `Rs.${p.default_lco_price}` : 'Price'}
                                    value={editVal}
                                    onChange={(e) => setPriceEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                                    data-testid={`lco-iptv-price-input-${p.package_id}`}
                                  />
                                </div>
                                <div className="w-20 flex items-center shrink-0">
                                  <Button size="sm" className="h-9 w-full" disabled={savingPriceKey === key} onClick={() => saveLcoOverride('iptv', p.package_id, editVal)} data-testid={`lco-iptv-price-save-${p.package_id}`}>
                                    {savingPriceKey === key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                                  </Button>
                                </div>
                              </div>
                            );
                          }) : <div className="text-xs text-slate-400 px-4 py-3">No IPTV packages</div>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          {/* Activations Tab */}
          <TabsContent value="activations">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Recent Activations ({activations.length})</CardTitle>
                  <Button variant="ghost" size="sm" onClick={fetchActivations}>
                    <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {activations.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Tv className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No activations yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {activations.map((act) => (
                      <div key={act.activation_id} className="flex items-center justify-between p-3 rounded-lg border" data-testid={`activation-${act.activation_id}`}>
                        <div>
                          <div className="font-medium text-sm">{act.pack_name}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                            <span>{act.customer_name}</span>
                            <span>{act.mobile_no}</span>
                            <span>{act.provider}</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {new Date(act.created_at).toLocaleDateString('en-IN')} | Expires: {act.end_date}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-right">
                          <div>
                            <div className="text-sm font-semibold">Rs.{act.amount_charged}</div>
                            {act.commission > 0 && (
                              <div className="text-xs text-green-600">+Rs.{act.commission} commission</div>
                            )}
                          </div>
                          {act.provider_status === 'activated' ? (
                            <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>
                          ) : act.provider_status === 'failed' ? (
                            <Badge className="bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" /> Failed</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add LCO Dialog */}
      <Dialog open={showAddLco} onOpenChange={setShowAddLco}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New LCO</DialogTitle>
            <DialogDescription>Add a sub-operator under your partner account</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Company Name *</Label><Input value={addForm.company_name} onChange={e => setAddForm(p => ({ ...p, company_name: e.target.value }))} placeholder="LCO Company Name" data-testid="add-lco-company" /></div>
            <div><Label>Contact Name *</Label><Input value={addForm.contact_name} onChange={e => setAddForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="Contact Person" data-testid="add-lco-contact" /></div>
            <div><Label>Phone *</Label><Input value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))} placeholder="10-digit mobile" maxLength={10} data-testid="add-lco-phone" /></div>
            <div><Label>Email</Label><Input value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} placeholder="email@example.com" data-testid="add-lco-email" /></div>
            <div><Label>Address</Label><Input value={addForm.address} onChange={e => setAddForm(p => ({ ...p, address: e.target.value }))} placeholder="Address" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>City</Label><Input value={addForm.city} onChange={e => setAddForm(p => ({ ...p, city: e.target.value }))} placeholder="City" /></div>
              <div><Label>State</Label><Input value={addForm.state} onChange={e => setAddForm(p => ({ ...p, state: e.target.value }))} placeholder="State" /></div>
              <div><Label>Pincode</Label><Input value={addForm.pincode} onChange={e => setAddForm(p => ({ ...p, pincode: e.target.value }))} placeholder="Pincode" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLco(false)}>Cancel</Button>
            <Button onClick={handleAddLco} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-add-lco">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />} Add LCO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable LCO Confirmation Dialog */}
      <Dialog open={!!disableTarget} onOpenChange={(o) => { if (!o) setDisableTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="disable-lco-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Disable LCO
            </DialogTitle>
            <DialogDescription>
              Remove <span className="font-semibold">{disableTarget?.company_name}</span> from your LCO list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              All data (wallet balances, subscriptions, transactions and custom pricing) is
              <span className="font-semibold"> preserved</span>. You can re-create this LCO later with the
              <span className="font-semibold"> same login details (phone/email)</span> to restore everything.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableTarget(null)} data-testid="disable-lco-cancel">Cancel</Button>
            <Button
              onClick={handleDisableLco}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="disable-lco-confirm"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />} Disable LCO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit LCO Wallet Dialog */}
      <Dialog open={showCreditWallet} onOpenChange={setShowCreditWallet}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Credit to LCO Wallet</DialogTitle>
            <DialogDescription>
              Transfer from your wallet to {selectedLco?.company_name || 'LCO'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 bg-blue-50 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Your Wallet Balance:</span>
                <span className="font-semibold text-blue-700">Rs.{(summary.partner_balance || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-600">LCO Current Balance:</span>
                <span className="font-semibold">{selectedLco ? `Rs.${(selectedLco.wallet_balance || 0).toFixed(2)}` : '-'}</span>
              </div>
            </div>
            <div>
              <Label>Amount (Rs.)</Label>
              <Input
                type="number" value={creditAmount}
                onChange={e => setCreditAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                data-testid="credit-amount"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditWallet(false)}>Cancel</Button>
            <Button onClick={handleCreditWallet} disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="submit-credit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ArrowUpRight className="h-4 w-4 mr-1" />} Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set LCO Pricing Dialog */}
      <Dialog open={!!showSetPricing} onOpenChange={() => setShowSetPricing(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set LCO Price</DialogTitle>
            <DialogDescription>{showSetPricing?.pack_name} ({showSetPricing?.provider})</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-1">
              <div className="flex justify-between"><span>Base Price:</span><span>Rs.{showSetPricing?.base_price}</span></div>
              <div className="flex justify-between text-blue-600 font-medium"><span>Your Cost:</span><span>Rs.{showSetPricing?.partner_price}</span></div>
            </div>
            <div>
              <Label>LCO Price (Rs.) *</Label>
              <Input
                type="number" value={pricingForm.lco_price}
                onChange={e => setPricingForm({ lco_price: e.target.value })}
                placeholder={`Min: ${showSetPricing?.partner_price}`}
                min={showSetPricing?.partner_price || 0}
                data-testid="lco-price-input"
              />
              {pricingForm.lco_price && parseFloat(pricingForm.lco_price) > (showSetPricing?.partner_price || 0) && (
                <p className="text-xs text-green-600 mt-1">
                  Your margin: Rs.{(parseFloat(pricingForm.lco_price) - (showSetPricing?.partner_price || 0)).toFixed(2)} per activation
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetPricing(null)}>Cancel</Button>
            <Button onClick={handleSetPricing} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-pricing">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Price'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate for LCO Dialog */}
      <Dialog open={!!showActivate} onOpenChange={() => setShowActivate(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Activate {showActivate?.pack_name}</DialogTitle>
            <DialogDescription>
              Provider: {showActivate?.provider} | LCO Price: Rs.{showActivate?.lco_price} | Your Margin: Rs.{showActivate?.margin?.toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Select LCO *</Label>
              <Select value={activateForm.lco_customer_id} onValueChange={v => setActivateForm(p => ({ ...p, lco_customer_id: v }))}>
                <SelectTrigger data-testid="select-lco"><SelectValue placeholder="Choose LCO" /></SelectTrigger>
                <SelectContent>
                  {lcos.map(l => (
                    <SelectItem key={l.customer_id} value={l.customer_id}>
                      {l.company_name} (Bal: Rs.{(l.wallet_balance || 0).toFixed(2)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subscriber Mobile No *</Label>
              <Input
                value={activateForm.mobile_no}
                onChange={e => setActivateForm(p => ({ ...p, mobile_no: e.target.value }))}
                placeholder="10-digit mobile"
                maxLength={10}
                data-testid="activate-mobile"
              />
            </div>
            <div>
              <Label>Subscriber Name</Label>
              <Input
                value={activateForm.subscriber_name}
                onChange={e => setActivateForm(p => ({ ...p, subscriber_name: e.target.value }))}
                placeholder="Name (optional)"
              />
            </div>
            <div>
              <Label>Subscriber Email</Label>
              <Input
                value={activateForm.subscriber_email}
                onChange={e => setActivateForm(p => ({ ...p, subscriber_email: e.target.value }))}
                placeholder="Email (optional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowActivate(null)}>Cancel</Button>
            <Button onClick={handleActivate} disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="submit-activate">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Play className="h-4 w-4 mr-1" />} Activate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LCO Detail Dialog */}
      <Dialog open={showLcoDetail} onOpenChange={setShowLcoDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{lcoDetail?.lco?.company_name || 'LCO Details'}</DialogTitle>
          </DialogHeader>
          {lcoDetail && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Contact:</span> <span className="font-medium">{lcoDetail.lco.contact_name}</span></div>
                <div><span className="text-slate-500">Phone:</span> <span className="font-medium">{lcoDetail.lco.contact_phone}</span></div>
                <div><span className="text-slate-500">Email:</span> <span className="font-medium">{lcoDetail.lco.email || '-'}</span></div>
              </div>

              {/* Wallets side-by-side */}
              <div className="grid grid-cols-2 gap-3">
                <div className="border rounded-lg p-3 bg-green-50/40">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Wallet className="h-3 w-3" /> OTT Wallet</div>
                  <div className="text-xl font-bold text-green-700">Rs.{(lcoDetail.wallet?.balance || 0).toFixed(2)}</div>
                </div>
                <div className="border rounded-lg p-3 bg-indigo-50/40">
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Tv className="h-3 w-3" /> IPTV Wallet</div>
                  <div className="text-xl font-bold text-indigo-700">Rs.{(lcoDetail.iptv_wallet?.balance || 0).toFixed(2)}</div>
                </div>
              </div>

              <Tabs value={detailTab} onValueChange={setDetailTab}>
                <TabsList className="bg-slate-50 flex-wrap h-auto">
                  <TabsTrigger value="ott_subs"><Tv className="h-3 w-3 mr-1" /> OTT Subs ({lcoDetail.active_subscriptions?.length || 0})</TabsTrigger>
                  <TabsTrigger value="iptv_subs"><Tv className="h-3 w-3 mr-1" /> IPTV Subs ({lcoDetail.iptv_active_subscriptions?.length || 0})</TabsTrigger>
                  <TabsTrigger value="ott_txn">OTT Txns</TabsTrigger>
                  <TabsTrigger value="iptv_txn">IPTV Txns</TabsTrigger>
                </TabsList>
                <TabsContent value="ott_subs">
                  {lcoDetail.active_subscriptions?.length > 0 ? lcoDetail.active_subscriptions.map(s => (
                    <div key={s.activation_id} className="p-2 border rounded text-xs mb-1">
                      <div className="font-medium">{s.pack_name} — {s.mobile_no}</div>
                      <div className="text-slate-500">Expires: {s.end_date}</div>
                    </div>
                  )) : <div className="text-xs text-slate-400 py-3 text-center">No active OTT subscriptions</div>}
                </TabsContent>
                <TabsContent value="iptv_subs">
                  {lcoDetail.iptv_active_subscriptions?.length > 0 ? lcoDetail.iptv_active_subscriptions.map(s => (
                    <div key={s.subscription_id || s.mobile_no} className="p-2 border rounded text-xs mb-1">
                      <div className="font-medium">{s.package_name || s.pack_name || 'IPTV'} — {s.mobile_no}</div>
                      <div className="text-slate-500">{s.subscriber_name || ''}{s.end_date ? ` · Expires: ${s.end_date}` : ''}</div>
                    </div>
                  )) : <div className="text-xs text-slate-400 py-3 text-center">No active IPTV subscriptions</div>}
                </TabsContent>
                <TabsContent value="ott_txn">
                  <ScrollArea className="h-48">
                    {lcoDetail.transactions?.length > 0 ? lcoDetail.transactions.map(t => (
                      <div key={t.transaction_id} className="flex items-center justify-between p-2 border-b text-xs">
                        <div>
                          <span className={t.type === 'credit' ? 'text-green-600' : 'text-red-600'}>{t.type === 'credit' ? '+' : '-'}Rs.{t.amount.toFixed(2)}</span>
                          <span className="text-slate-500 ml-2">{t.description}</span>
                        </div>
                        <span className="text-slate-400">{new Date(t.created_at).toLocaleDateString('en-IN')}</span>
                      </div>
                    )) : <div className="text-xs text-slate-400 py-3 text-center">No OTT transactions</div>}
                  </ScrollArea>
                </TabsContent>
                <TabsContent value="iptv_txn">
                  <ScrollArea className="h-48">
                    {lcoDetail.iptv_transactions?.length > 0 ? lcoDetail.iptv_transactions.map(t => (
                      <div key={t.transaction_id} className="flex items-center justify-between p-2 border-b text-xs">
                        <div>
                          <span className={t.type === 'credit' ? 'text-green-600' : 'text-red-600'}>{t.type === 'credit' ? '+' : '-'}Rs.{(t.amount || 0).toFixed(2)}</span>
                          <span className="text-slate-500 ml-2">{t.description}</span>
                        </div>
                        <span className="text-slate-400">{new Date(t.created_at).toLocaleDateString('en-IN')}</span>
                      </div>
                    )) : <div className="text-xs text-slate-400 py-3 text-center">No IPTV transactions</div>}
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLcoDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set IPTV Price Dialog */}
      <Dialog open={!!showSetIptvPrice} onOpenChange={() => setShowSetIptvPrice(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set IPTV LCO Price</DialogTitle>
            <DialogDescription>{showSetIptvPrice?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Your Cost:</span><span className="font-semibold">Rs.{showSetIptvPrice?.partner_price}</span></div>
              <div className="flex justify-between mt-1"><span className="text-slate-600">Base Price:</span><span className="font-semibold">Rs.{showSetIptvPrice?.base_price}</span></div>
            </div>
            <div>
              <Label>Your Price for LCOs (Rs.)</Label>
              <Input type="number" value={iptvPriceForm.lco_price} onChange={(e) => setIptvPriceForm({ lco_price: e.target.value })} placeholder="Enter price" min="0" data-testid="iptv-set-lco-price-input" />
              {iptvPriceForm.lco_price && parseFloat(iptvPriceForm.lco_price) > (showSetIptvPrice?.partner_price || 0) && (
                <p className="text-xs text-green-600 mt-1">Margin: Rs.{(parseFloat(iptvPriceForm.lco_price) - (showSetIptvPrice?.partner_price || 0)).toFixed(2)}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetIptvPrice(null)}>Cancel</Button>
            <Button onClick={handleSetIptvPrice} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-iptv-price-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Price
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credit IPTV Wallet Dialog */}
      <Dialog open={showCreditIptv} onOpenChange={setShowCreditIptv}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Credit to IPTV Wallet</DialogTitle>
            <DialogDescription>
              Transfer from your IPTV wallet to {selectedLco?.company_name || 'LCO'}'s IPTV wallet
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 bg-indigo-50 rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Your IPTV Balance:</span>
                <span className="font-semibold text-indigo-700">Rs.{(iptvPartnerBalance || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between mt-1">
                <span className="text-slate-600">LCO IPTV Balance:</span>
                <span className="font-semibold">{selectedLco ? `Rs.${(selectedLco.iptv_wallet_balance || 0).toFixed(2)}` : '-'}</span>
              </div>
            </div>
            <div>
              <Label>Amount (Rs.)</Label>
              <Input
                type="number" value={creditIptvAmount}
                onChange={e => setCreditIptvAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                data-testid="credit-iptv-amount"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreditIptv(false)}>Cancel</Button>
            <Button onClick={handleCreditIptvWallet} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-credit-iptv">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Tv className="h-4 w-4 mr-1" />} Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
